import {
  HomePageProxy,
  PlantationProblematicSpotViewModel,
  PlantationProblematicSpotViewModelListResponseResult,
  UserEnterpriseInfoViewModel,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { toCanonicalServicesUrl } from '@/app/features/services/services-route.util';
import { WidgetInfoBoxComponent } from '../home-warnings-and-alerts/widget-info-box/widget-info-box.component';

@Component({
  selector: 'app-home-problematic-spots',
  imports: [SharedModule, WidgetInfoBoxComponent],
  templateUrl: './home-problematic-spots.component.html',
  styleUrl: './home-problematic-spots.component.less',
})
export class HomeProblematicSpotsComponent implements OnChanges {
  @Input() selectedEnterprise: UserEnterpriseInfoViewModel;
  @Input() hasAnyPremiumForSpotDetection = false;
  @Output() readonly getPremiumEvent = new EventEmitter<void>();
  @Output() readonly analyticsProblematicSpotsLoaded = new EventEmitter<{
    problematicSpotsCount: number;
    fetchingError: boolean;
  }>();

  problematicSpotsLoading = true;
  noPremiumSpotImage = '/assets/images/no-premium/problematic-spot.png';
  problematicSpots: PlantationProblematicSpotViewModel[] = [];
  singlePlantationSpot: PlantationProblematicSpotViewModel | null = null;
  singlePlantationLandName: string | undefined;

  fetchingError = false;

  constructor(
    private _homeProxy: HomePageProxy,
    private _router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['hasAnyPremiumForSpotDetection'] || changes?.['selectedEnterprise']) {
      this.problematicSpots = [];
      this.resolveSinglePlantation();
      if (this.selectedEnterprise && this.hasAnyPremiumForSpotDetection) {
        this.getProblematicSpots();
      }
    }
  }

  getProblematicSpots() {
    this.fetchingError = false;
    this.problematicSpotsLoading = true;
    this._homeProxy
      .homePage_GetProblemaicSpotByEnterprise(this.selectedEnterprise?.enterpriseId)
      .pipe(
        finalize(() => {
          this.problematicSpotsLoading = false;
        })
      )
      .subscribe((res: PlantationProblematicSpotViewModelListResponseResult) => {
        if (res.isSuccess) {
          const totalProblematicSpots =
            res.result?.reduce(
              (acc, spot) => acc + (spot.problematicSpotList ? spot.problematicSpotList.length : 0),
              0
            ) ?? 0;
          this.problematicSpots = [...res.result];
          this.resolveSinglePlantation();
          this.analyticsProblematicSpotsLoaded.emit({
            problematicSpotsCount: totalProblematicSpots,
            fetchingError: false,
          });
        } else {
          this.fetchingError = true;
          this.analyticsProblematicSpotsLoaded.emit({
            problematicSpotsCount: 0,
            fetchingError: true,
          });
        }
      });
  }

  goToCropMonitoring(plantationId: string | undefined): void {
    if (!plantationId) {
      return;
    }

    this._router.navigateByUrl(toCanonicalServicesUrl(plantationId, 'crop-monitoring'));
  }

  /**
   * The problematic spot endpoint does not return a land name, so it is resolved from the
   * already loaded enterprise tree instead of issuing another request.
   */
  private resolveSinglePlantation(): void {
    const singleSpot = this.problematicSpots?.length === 1 ? this.problematicSpots[0] : null;
    this.singlePlantationSpot = singleSpot;
    this.singlePlantationLandName = singleSpot
      ? this.selectedEnterprise?.lands?.find((land) =>
          land.plantations?.some((plantation) => plantation.id === singleSpot.plantationId)
        )?.name
      : undefined;
  }
}
